import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from '../../../../hooks';
import { ExecutiveSummaryData } from '../ExecutiveSummary/executive-summary';
import { messages as executiveSummaryMessages } from '../ExecutiveSummary/messages';
import { selectDefaultCrdId } from '../ExecutiveSummary/selectDefaultCrdId';
import { useCrdTemplateSectionAvailability } from '../ExecutiveSummary/useCrdTemplateSectionAvailability';
import { useExecutiveSummaryGeneration } from '../ExecutiveSummary/useExecutiveSummaryGeneration';
import { didNewActionItemsLand } from './didNewActionItemsLand';
import { hasDuplicateActiveStandardCarePlans } from './minimalActionPlan';
import { useCanGenerateWithAi } from './useCanGenerateWithAi';

const messages = defineMessages({
  emptyRunSuccess: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.emptyRunSuccess',
    defaultMessage:
      'No new action items were added. You can add items yourself or generate again.',
  },
});

type GenerationUiPhase = 'idle' | 'in-flight' | 'awaiting-refetch';

export const useGenerateActionPlan = ({
  patientId,
  executiveSummary,
  fetchCarePlan,
  isRefetching = false,
  carePlans,
}: {
  patientId: string;
  executiveSummary: ExecutiveSummaryData | null | undefined;
  fetchCarePlan: () => void;
  isRefetching?: boolean;
  carePlans?: fhir.CarePlan[];
}) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const { showGenerate, compositeReportDefinitions } =
    useCanGenerateWithAi(carePlans);
  const { hasTemplateSections } = useCrdTemplateSectionAvailability(
    compositeReportDefinitions,
  );
  const itemsBeforeGenerateRef = useRef<fhir.CarePlanActivity[] | undefined>(
    undefined,
  );
  const executiveSummaryRef = useRef(executiveSummary);
  executiveSummaryRef.current = executiveSummary;
  const sawRefetchRef = useRef(false);
  const wasGeneratingRef = useRef(false);
  const [phase, setPhase] = useState<GenerationUiPhase>('idle');

  const { isGenerating, generateExecutiveSummary } =
    useExecutiveSummaryGeneration({
      patientId,
      onGenerationSuccess: () => {
        sawRefetchRef.current = false;
        setPhase('awaiting-refetch');
        fetchCarePlan();
      },
      onGenerationFailure: () => {
        itemsBeforeGenerateRef.current = undefined;
        sawRefetchRef.current = false;
        wasGeneratingRef.current = false;
        setPhase('idle');
        notify(
          formatMessage(executiveSummaryMessages.generateFailure),
          'error',
        );
      },
      onGenerationConflict: () => {
        itemsBeforeGenerateRef.current = undefined;
        sawRefetchRef.current = false;
        wasGeneratingRef.current = false;
        setPhase('idle');
        notify(
          formatMessage(executiveSummaryMessages.generateConflict),
          'warning',
        );
      },
    });

  useEffect(() => {
    if (isGenerating && phase === 'idle') {
      itemsBeforeGenerateRef.current =
        executiveSummaryRef.current?.carePlan?.activity;
      setPhase('in-flight');
    }
  }, [isGenerating, phase]);

  useEffect(() => {
    if (isGenerating) {
      wasGeneratingRef.current = true;
      return;
    }
    if (phase !== 'in-flight' || !wasGeneratingRef.current) {
      return;
    }
    const timeoutId = setTimeout(() => {
      itemsBeforeGenerateRef.current = undefined;
      sawRefetchRef.current = false;
      wasGeneratingRef.current = false;
      setPhase('idle');
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [phase, isGenerating]);

  useEffect(() => {
    if (phase !== 'awaiting-refetch') {
      return;
    }
    if (isRefetching) {
      sawRefetchRef.current = true;
      return;
    }
    if (!sawRefetchRef.current) {
      return;
    }

    const nothingNewLanded = !didNewActionItemsLand(
      itemsBeforeGenerateRef.current,
      executiveSummary?.carePlan?.activity,
    );
    itemsBeforeGenerateRef.current = undefined;
    sawRefetchRef.current = false;
    wasGeneratingRef.current = false;
    setPhase('idle');
    if (nothingNewLanded) {
      notify(formatMessage(messages.emptyRunSuccess), 'success');
    }
  }, [executiveSummary, formatMessage, isRefetching, notify, phase]);

  const generateActionPlan = useCallback(async () => {
    if (
      isGenerating ||
      phase !== 'idle' ||
      hasDuplicateActiveStandardCarePlans(carePlans)
    ) {
      return;
    }

    const crdId =
      executiveSummary?.compositeReportDefinitionDurableId ??
      selectDefaultCrdId(compositeReportDefinitions, hasTemplateSections);
    if (!crdId) {
      return;
    }

    itemsBeforeGenerateRef.current = executiveSummary?.carePlan?.activity;
    setPhase('in-flight');

    const hasImpressions = (executiveSummary?.impressions.length ?? 0) > 0;
    const isRegenerate = Boolean(
      executiveSummary?.hasContent && executiveSummary.status === 'active',
    );

    try {
      await generateExecutiveSummary(
        {
          crdId,
          requestedSummaries: [],
          skipActionItems: false,
        },
        {
          isRegenerate,
          surface: 'action_plan',
          hasImpressions,
        },
      );
    } catch (err) {
      console.error(err);
      itemsBeforeGenerateRef.current = undefined;
      sawRefetchRef.current = false;
      wasGeneratingRef.current = false;
      setPhase('idle');
      notify(formatMessage(executiveSummaryMessages.generateFailure), 'error');
    }
  }, [
    carePlans,
    compositeReportDefinitions,
    executiveSummary,
    formatMessage,
    generateExecutiveSummary,
    hasTemplateSections,
    isGenerating,
    notify,
    phase,
  ]);

  return {
    generateActionPlan,
    isGenerating: isGenerating || phase !== 'idle',
    showGenerate,
  };
};
